import React from 'react';

import type { LiteDriverInfo, PlannerScenario } from '@hopdrive/feature-move-planner';

import { useGetScheduleScenariosQuery } from '@features/schedule/queries/getScheduleScenarios.query.generated';
import { useGetDriversQuery } from '@features/drivers/queries/getDrivers.query.generated';
import { useMultipleDriverSchedules } from '@features/drivers/hooks/useDriverSchedule';
import { processDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';

/** The rooftop's scenarios on one day, fetched fresh, for the planner's time form. */
export function useDealerDayScenarios({
  customerId,
  start,
  end,
  skip,
}: {
  customerId: number;
  start: string;
  end: string;
  skip: boolean;
}): { scenarios: PlannerScenario[]; loading: boolean } {
  const { data, loading } = useGetScheduleScenariosQuery({
    variables: { start, end, customerId },
    skip,
    fetchPolicy: 'network-only',
  });
  return React.useMemo(() => ({ scenarios: (data?.scenarios || []) as unknown as PlannerScenario[], loading }), [data, loading]);
}

/** A Lite rooftop's active drivers with their processed weekly schedules; undefined until known. */
export function useDealerLiteDrivers(customerId: number | undefined, isLite: boolean): LiteDriverInfo[] | undefined {
  const { data: driversData } = useGetDriversQuery({
    variables: { customerId: customerId || 0 },
    skip: !customerId || !isLite,
  });

  // Get active driver IDs for Lite tier
  const drivers = driversData?.drivers;
  const activeDriverIds = React.useMemo(() => {
    if (!isLite || !drivers) return [];
    return drivers
      .filter(d => d.status === 'online' || d.status === 'offline' || d.status === 'training')
      .map(d => d.id);
  }, [isLite, drivers]);

  // Fetch schedules for all active drivers (Lite tier only)
  const { schedulesByDriverId } = useMultipleDriverSchedules(activeDriverIds);

  return React.useMemo((): LiteDriverInfo[] | undefined => {
    if (!isLite || activeDriverIds.length === 0) return undefined;

    return activeDriverIds.map(driverId => {
      const rawSchedule = schedulesByDriverId.get(driverId);
      const processedSchedule =
        rawSchedule && rawSchedule.length > 0 ? processDriverSchedule(rawSchedule, driverId.toString()) : undefined;
      return {
        id: driverId,
        schedule: processedSchedule,
      };
    });
  }, [isLite, activeDriverIds, schedulesByDriverId]);
}
